/**
 *  TopbarMobileMenu prints the menu content for authenticated user or
 * shows login actions for those who are not authenticated.
 */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Sidenav, Nav } from 'rsuite';
import { useIntl } from 'react-intl';
import { useHistory, useLocation } from 'react-router-dom/cjs/react-router-dom.min';
import classNames from 'classnames';

import routeConfiguration from '../../../routing/routeConfiguration';
import { useConfiguration } from '../../../context/configurationContext';
import { FormattedMessage } from '../../../util/reactIntl';
import { propTypes } from '../../../util/types';
import { ensureCurrentUser } from '../../../util/data';
import { createResourceLocatorString } from '../../../util/routes';
import {
  getNeutralizedString,
  isArrayLength,
  sortWebinarEventsByDate,
  switchAppLanguage,
} from '../../../util/genericHelpers';

import { AvatarLarge, Button, ExternalLink, NamedLink } from '../../../components';
import englishImage from '../../LocaleMenu/flags/english.png';
import greekImage from '../../LocaleMenu/flags/greek.png';
import germanImage from '../../LocaleMenu/flags/german.png';
import { IoChevronDownOutline } from 'react-icons/io5';
import { IoChevronUpOutline } from 'react-icons/io5';

import css from './TopbarMobileMenu.module.css';
import '../../../styles/rsuite.min.css';
import Icons from '../../Icons/Icons';
import {
  trackArticleCategoryClicked,
  trackChatbotEvent,
  trackLogInClicked,
  trackSessionsClicked,
  trackWellnessProgramClicked,
} from '../../../util/mixpanelHelpers';
import { MIXPANEL_EVENTS, TOPBAR_GUIDE_OPTIONS } from '../../../util/enums';
import { useSignInContext } from '../../../context/SignInContext';

const localeOptions = [
  { key: 'en', label: 'EN', alt: 'English', flag: englishImage },
  { key: 'el', label: 'GR', alt: 'Greek', flag: greekImage },
  { key: 'de', label: 'DE', alt: 'German', flag: germanImage },
];

const TopbarMobileMenu = props => {
  const { isAuthenticated, currentUser, events, onClose } = props;

  const intl = useIntl();
  const history = useHistory();
  const location = useLocation();
  const user = ensureCurrentUser(currentUser);
  const config = useConfiguration();
  const { siteFacebookPage, siteInstagramPage, siteLinkedInPage } = config;
  const [isExpanded, setExpanded] = useState(false);
  const { openSignIn } = useSignInContext();
  const handleClose = () => {
    if (onClose) {
      onClose();
    }
  };

  const handleLanguageChange = (event, locale) => {
    event.preventDefault();
    switchAppLanguage({
      locale,
      pathname: location.pathname,
      search: location.search,
      hash: location.hash,
    });
    handleClose();
  };

  if (!isAuthenticated) {
    const login = (
      <div
        onClick={() => {
          trackLogInClicked(MIXPANEL_EVENTS?.LOGIN_OPENED);
          if (openSignIn) {
            openSignIn({ defaultToLogin: true, fullScreenOnMobile: true });
          }
          if (onClose) {
            onClose();
          }
        }}
        className={css.loginLink}
      >
        <FormattedMessage id="TopbarMobileMenu.loginLink" />
      </div>
    );

    return (
      <>
        <div className={css.root}>
          <div className={css.content}>
            <div className={css.authenticationGreeting}>
              <div className={css.authBtns}>
                {login}
              </div>
            </div>
            <div className={css.browseSec}>
              <ul className={css.list}>
                <li className={css.listItem}>
                  <NamedLink name="AiPage" className={css.navLink} onClick={handleClose}>
                    <FormattedMessage id="TopbarDesktop.aiPageLink" />
                  </NamedLink>
                </li>
                <li className={css.listItem}>
                  <NamedLink name="HealthPlansPage" className={css.navLink} onClick={handleClose}>
                    <FormattedMessage id="TopbarDesktop.heathLink" />
                  </NamedLink>
                </li>
                <li className={css.listItem}>
                  <NamedLink
                    name="HospitalsAndClinicsPage"
                    className={css.navLink}
                    onClick={handleClose}
                  >
                    <FormattedMessage id="TopbarDesktop.hospitalsAndClinicsPage" />
                  </NamedLink>
                </li>
                <li className={css.listItem}>
                  <NamedLink name="EmployersPage" className={css.navLink} onClick={handleClose}>
                    <FormattedMessage id="TopbarDesktop.companiesLink" />
                  </NamedLink>
                </li>
                <li className={css.listItem}>
                  <NamedLink name="DoctorsPage" className={css.navLink} onClick={handleClose}>
                    <FormattedMessage id="TopbarDesktop.doctorsLink" />
                  </NamedLink>
                </li>
                <li className={css.listItem}>
                  <NamedLink name="AboutPage" className={css.navLink} onClick={handleClose}>
                    <FormattedMessage id="Footer.toAboutPage" />
                  </NamedLink>
                </li>
              </ul>
            </div>
            <div className={css.languageSection}>
              <div className={css.languageSectionHeader}>
                <FormattedMessage id="TopbarDesktop.languages" />
              </div>
              <div className={css.languageButtons}>
                {localeOptions.map(option => (
                  <button
                    key={option.key}
                    className={classNames(css.languageButton, {
                      [css.languageButtonActive]: config.localization.locale === option.key,
                    })}
                    type="button"
                    onClick={event => handleLanguageChange(event, option.key)}
                  >
                    <img src={option.flag} alt={option.alt} className={css.languageFlag} />
                    <span>{option.label}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
          <div className={css.footerSocialIcon}>
            <ul>
              <li>
                <ExternalLink
                  href={siteFacebookPage}
                  name="PrivacyPolicyPage"
                  className={css.facebook}
                >
                  <Icons name="facebook" />
                </ExternalLink>
              </li>
              <li>
                <ExternalLink
                  href={siteInstagramPage}
                  name="PrivacyPolicyPage"
                  className={css.instagram}
                >
                  <Icons name="instagram" />
                </ExternalLink>
              </li>
              <li>
                <ExternalLink
                  href={siteLinkedInPage}
                  name="PrivacyPolicyPage"
                  className={css.linkedin}
                >
                  <Icons name="linkedin" />
                </ExternalLink>
              </li>
              <li>
                <ExternalLink
                  href="https://www.youtube.com/@GetVivianLab/videos"
                  name="youtube"
                  className={css.linkedin}
                >
                  <Icons name="youtubeIcon" />
                </ExternalLink>
              </li>
              <li>
                <ExternalLink
                  href="mailto:info@vivianlab.com"
                  name="PrivacyPolicyPage"
                  className={css.email}
                >
                  <Icons name="email" />
                </ExternalLink>
              </li>
            </ul>
            <p>© 2026 VivianLab</p>
          </div>
        </div>
      </>
    );
  }

  const displayName = user.attributes.profile.firstName;

  return (
    <div className={css.root}>
      <div className={css.content}>
        {isAuthenticated && isArrayLength(sortWebinarEventsByDate(events)) && (
          <div
            className={css.link}
            onClick={() => {
              trackSessionsClicked(MIXPANEL_EVENTS?.EVENTS_NAV_CLICKED, null, null, null);
              history.push(createResourceLocatorString('EventsPage', routeConfiguration(), {}, {}));
              handleClose();
            }}
          >
            <FormattedMessage id="TopbarDesktop.freeIndividualLink" />
          </div>
        )}
        <a href="/resources#modules" className={css.link} onClick={handleClose}>
          <FormattedMessage id="TopbarDesktop.modulesLink" />
        </a>
        <a
          href="/resources#symptoms"
          name="MenopauseResourcePage"
          className={css.link}
          onClick={handleClose}
        >
          <FormattedMessage id="TopbarDesktop.symptomsLink" />
        </a>

        <div
          className={css.link}
          onClick={() => {
            trackWellnessProgramClicked(MIXPANEL_EVENTS?.ALL_PROGRAMS_CLICKED, null, null);
            history.push(
              createResourceLocatorString('WellnessProgramPage', routeConfiguration(), {}, {})
            );
            handleClose();
          }}
        >
          <FormattedMessage id="TopbarDesktop.wellnessProgramLink" />
        </div>

        <div className={css.wrritenLinkSection}>
          <div className={css.wrritenLink} onClick={() => setExpanded(!isExpanded)}>
            <span className={css.wrrtenLinkName}>
              {intl.formatMessage({ id: 'TopbarDesktop.writtenLink' })}
            </span>
            {isExpanded ? (
              <span>
                <IoChevronUpOutline className={css.downArrowIcon} />
              </span>
            ) : (
              <span>
                <IoChevronDownOutline className={css.downArrowIcon} />
              </span>
            )}
          </div>
          <div
            className={isExpanded ? css.subWrritenLinkSection : css.subWrritenLinkSectionDisable}
          >
            <p
              key="HormoneBalanceResourcePage"
              className={css.subWrritenLink1}
              onClick={() => {
                trackArticleCategoryClicked(
                  MIXPANEL_EVENTS?.ARTICLE_CATEGORY_NAV_CLICKED,
                  TOPBAR_GUIDE_OPTIONS?.Menopause
                );
                history.push(
                  createResourceLocatorString(
                    'WrittenGuidesPage',
                    routeConfiguration(),
                    { slug: getNeutralizedString('menopause') },
                    {}
                  )
                );
                handleClose();
              }}
            >
              {intl.formatMessage({ id: 'TopbarDesktop.hormoneBalanceLink' })}
            </p>

            <p
              key="NutritionResourcePage"
              className={css.subWrritenLink}
              onClick={() => {
                trackArticleCategoryClicked(
                  MIXPANEL_EVENTS?.ARTICLE_CATEGORY_NAV_CLICKED,
                  TOPBAR_GUIDE_OPTIONS?.Nutrition
                );
                history.push(
                  createResourceLocatorString(
                    'WrittenGuidesPage',
                    routeConfiguration(),
                    { slug: getNeutralizedString('nutrition') },
                    {}
                  )
                );
                handleClose();
              }}
            >
              {intl.formatMessage({ id: 'TopbarDesktop.nutritionTipsRecipesLink' })}
            </p>

            <p
              key="PsychologyResourcePage"
              className={css.subWrritenLink}
              onClick={() => {
                trackArticleCategoryClicked(
                  MIXPANEL_EVENTS?.ARTICLE_CATEGORY_NAV_CLICKED,
                  TOPBAR_GUIDE_OPTIONS?.Psychology
                );
                history.push(
                  createResourceLocatorString(
                    'WrittenGuidesPage',
                    routeConfiguration(),
                    { slug: getNeutralizedString('psychology') },
                    {}
                  )
                );
                handleClose();
              }}
            >
              {intl.formatMessage({ id: 'TopbarDesktop.psychologyHypnotherapyLink' })}
            </p>

            <p
              key="FertilityResourcePage"
              className={css.subWrritenLink}
              onClick={() => {
                trackArticleCategoryClicked(
                  MIXPANEL_EVENTS?.ARTICLE_CATEGORY_NAV_CLICKED,
                  TOPBAR_GUIDE_OPTIONS?.Fertility
                );
                history.push(
                  createResourceLocatorString(
                    'WrittenGuidesPage',
                    routeConfiguration(),
                    { slug: getNeutralizedString('fertility') },
                    {}
                  )
                );
                handleClose();
              }}
            >
              {intl.formatMessage({ id: 'TopbarDesktop.fertilityLink' })}
            </p>
          </div>
        </div>

        <a
          href="https://www.facebook.com/getvivianlab"
          className={classNames(css.link, css.noBorderBottom)}
          target="_blank"
          onClick={handleClose}
        >
          {intl.formatMessage({ id: 'TopbarDesktop.joinCommunityLink' })}
        </a>
        <Button
          className={css.aiCoachBtn}
          onClick={() => {
            trackChatbotEvent(MIXPANEL_EVENTS?.AI_OPENED, {
              buttonClicked: intl.formatMessage({ id: 'TopbarDesktop.aiPage' }),
            });
            history.push(
              createResourceLocatorString('AIConsultPage', routeConfiguration(), {}, {})
            );
            handleClose();
          }}
        >
          <Icons name="aiButtonStar" /> {intl.formatMessage({ id: 'TopbarDesktop.aiPage' })}
        </Button>
        <div className={css.spacer} />
      </div>
    </div>
  );
};

TopbarMobileMenu.defaultProps = {
  currentUser: null,
  notificationCount: 0,
  currentPage: null,
  events: [],
  onSubmitLogin: null,
  loginInProgress: false,
  loginError: null,
  onClose: null,
};

const { bool, func, number, string, array } = PropTypes;

TopbarMobileMenu.propTypes = {
  isAuthenticated: bool.isRequired,
  currentUserHasListings: bool.isRequired,
  currentUser: propTypes.currentUser,
  currentPage: string,
  notificationCount: number,
  onLogout: func.isRequired,
  events: array,
  onSubmitLogin: func,
  loginInProgress: bool,
  loginError: propTypes.error,
  onClose: func,
};

export default TopbarMobileMenu;
